import { useTheme } from "@mui/material";
import { CallbackDataParams } from "echarts/types/dist/shared";
import _ from "lodash";
import { useCallback, useEffect, useMemo } from "react";

import { Stack } from "@/Components/v2/atoms/Stack";
import { GroupedSatCurveLegendCards } from "@/Components/v2/molecule/LegendCards";
import { MarkerLegends } from "@/Components/v2/molecule/MarkerLegends";
import ChartWithMarkers from "@/Components/v2/organism/ChartWithMarkers/ChartWithMarkers";
import { Config, HIDE_DECIMAL, NO_SYMBOL } from "@/hooks/useConfig";
import {
  CurveType,
  InvestmentRating,
  InvestmentRatingMap,
  markerMap,
} from "@/pages/investment-levels/constants/maps";

import { formatTooltip } from "../../helpers/curveTooltip";
import { getCurveDescription, getSubTitle, getTitle } from "../../helpers/satCurve";
import { useInvestmentLevelsStore } from "../../store/useInvestmentLevelsStore";
import { ComparisonMode } from "../../types/comparisonMode";
import { GraphDataDTO } from "../../types/dto";
import { CurveTypeSelector } from "../Sections/CurveTypeSelector";

interface ChartContainerProps {
  curves: GraphDataDTO[];
  config: Config;
}

export interface GroupedCurveData {
  id: string;
  label: string;
  isShown: boolean;
  data: { id: string; isActive: boolean }[];
}

const CHART_TITLE = "Weekly Marketing Spend";

const GroupCurveContainer: React.FC<ChartContainerProps> = ({ curves, config }) => {
  const { setActiveCurveIds } = useInvestmentLevelsStore();
  const { comparisonMode, targetNodeUUID } = useInvestmentLevelsStore((state) => state.filters);
  const { activeCurveIds, curveType } = useInvestmentLevelsStore((state) => state.curves);
  const theme = useTheme();

  const markerLegends = Object.values(markerMap);
  const formatCurrency = config.currency.formatCurrency;

  useEffect(() => {
    // Reset active curves when comparison mode changes or on initial load with no active curves
    if (curves.length > 0) {
      setActiveCurveIds(curves.map((curve) => curve.id));
    }
  }, [targetNodeUUID, curves]);

  const curvesWithoutDescriptions = [InvestmentRating.INSUFFICIENT_DATA, InvestmentRating.BALANCED];

  // Group curves by rating for legend display
  const legends = useMemo(() => {
    const investmentRatings = Object.keys(InvestmentRatingMap);
    return investmentRatings.map((rating) => {
      const curvesInGroup = curves.filter((curve) => curve.rating === rating);
      return {
        id: rating,
        label: InvestmentRatingMap[rating as keyof typeof InvestmentRatingMap].label,
        tooltip: InvestmentRatingMap[rating as keyof typeof InvestmentRatingMap].tooltip,
        isShown: curvesInGroup.some((curve) => activeCurveIds?.includes(curve.id)),
        isDisabled: rating === InvestmentRating.INSUFFICIENT_DATA,
        data: curvesInGroup.map((curve) => ({
          id: curve.id,
          color: comparisonMode === ComparisonMode.GEOGRAPHY ? curve.secondaryColor : curve.color,
          title: getTitle(curve, comparisonMode) || "",
          subTitle: getSubTitle(curve, comparisonMode) || "",
          description: curvesWithoutDescriptions.includes(curve.rating)
            ? ""
            : getCurveDescription(curve, formatCurrency),
          isActive: activeCurveIds?.includes(curve.id),
          isDisabled: curve.rating === InvestmentRating.INSUFFICIENT_DATA,
        })),
      };
    });
  }, [curves, activeCurveIds]);

  // Toggle individual curve visibility
  const handleLegendItemClick = (curveId: string) => {
    const newActiveCurveIds = activeCurveIds.includes(curveId)
      ? activeCurveIds.filter((id) => id !== curveId)
      : [...activeCurveIds, curveId];

    setActiveCurveIds(newActiveCurveIds);
  };

  // Toggle all curves in a rating group
  const handleHideToggle = (ratingId: string) => {
    const curvesInGroup = curves.filter((curve) => curve.rating === ratingId);
    const curveIdsInGroup = curvesInGroup.map((curve) => curve.id);

    // Check if ANY curves in the group are active (not all)
    const hasActiveCurves = curveIdsInGroup.some((id) => activeCurveIds?.includes(id));

    const newActiveCurveIds = hasActiveCurves
      ? activeCurveIds.filter((id) => !curveIdsInGroup.includes(id)) // Hide all curves in group
      : [...activeCurveIds, ...curveIdsInGroup]; // Show all curves in group

    setActiveCurveIds(newActiveCurveIds);
  };

  const filteredCurves = curves.filter(
    (curve) =>
      curve.rating !== null &&
      curve.rating !== InvestmentRating.INSUFFICIENT_DATA &&
      activeCurveIds.includes(curve.id)
  );

  const tooltipFormatter = useCallback(
    (params: CallbackDataParams[]) => {
      return formatTooltip({
        theme,
        params,
        graphData: filteredCurves,
        curveType,
        comparisonMode,
        formatCurrency,
        config,
      });
    },
    [theme, filteredCurves, curveType, comparisonMode, formatCurrency, config]
  );

  const curvesData = useMemo(() => {
    return filteredCurves
      .map((curve) => {
        const curveData = curve.curveTypes[curveType];
        if (!curveData) return null;
        return {
          ...curveData,
          color: comparisonMode === ComparisonMode.GEOGRAPHY ? curve.secondaryColor : curve.color,
          curveCoordinates: curveData.curveCoordinates || [],
          markData: curveData.markData || [],
          id: curve.id + "-" + curveType,
        };
      })
      .filter((curve): curve is NonNullable<typeof curve> => curve !== null);
  }, [filteredCurves, curveType]);

  const revenueFormatterOptions = config.showReturnInsteadOfRevenue
    ? { ...NO_SYMBOL, ...HIDE_DECIMAL }
    : HIDE_DECIMAL;
  const yieldFormatterOptions = config.showReturnInsteadOfRevenue ? NO_SYMBOL : {};
  const xAxisFormatter = (value: number) => formatCurrency(value, HIDE_DECIMAL);
  const yAxisFormatter = (value: number) => {
    if (curveType === CurveType.REVENUE) return formatCurrency(value, revenueFormatterOptions);
    if (curveType === CurveType.YIELD) return formatCurrency(value, yieldFormatterOptions);
    return value.toString();
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <CurveTypeSelector />
        <MarkerLegends markerLegends={markerLegends} />
      </Stack>
      <ChartWithMarkers
        title={CHART_TITLE}
        curves={curvesData}
        markerMap={markerMap}
        tooltipFormatter={tooltipFormatter}
        xAxisFormatter={xAxisFormatter}
        yAxisFormatter={yAxisFormatter}
      />
      <GroupedSatCurveLegendCards
        legends={legends}
        handleHideToggle={handleHideToggle}
        handleLegendItemClick={handleLegendItemClick}
      />
    </>
  );
};

export default GroupCurveContainer;
